import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { authService } from '../services/auth'
import api from '../services/api'
import MarketingHeader from '../components/MarketingHeader'
import './LoginPage.scss'

const LoginPage = () => {
  const { t } = useTranslation('auth')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [loading, setLoading] = useState(false)

  // SSO state
  const [step, setStep] = useState('email') // 'email', 'password', 'sso', 'both', 'locked', 'unverified'
  const [loginMethod, setLoginMethod] = useState(null)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState('')

  const { login, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Check for success message from password reset
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message)
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  // Redirect after login - check for explicit post-login redirect first
  // (set by <AcceptInvitation /> for logged-out invitees in Task 17), then
  // pending billing plan, then default to the dashboard.
  useEffect(() => {
    if (!authLoading && user) {
      // Post-login redirect (Task 17 / #49): stashed by AcceptInvitation
      // when a logged-out invitee accepts an invite — we send them back to
      // the same landing URL so the UI reflects the newly-active
      // membership. The key name is the canonical
      // `POST_LOGIN_REDIRECT_KEY` from `acceptInvitationHelpers.js`; we
      // inline the literal here to avoid pulling the AcceptInvitation
      // module into the eagerly-loaded LoginPage bundle.
      const postLoginRedirect = localStorage.getItem('postLoginRedirect')
      if (postLoginRedirect) {
        localStorage.removeItem('postLoginRedirect')
        // Only honor same-origin relative paths — prevents open-redirect
        // abuse via a crafted localStorage value. Reject `//evil.com`
        // (protocol-relative) AND `/\evil.com` (some browsers/parsers
        // treat a backslash like a slash).
        const isSafeInternalPath =
          typeof postLoginRedirect === 'string' &&
          postLoginRedirect.startsWith('/') &&
          !postLoginRedirect.startsWith('//') &&
          !postLoginRedirect.startsWith('/\\')
        if (isSafeInternalPath) {
          navigate(postLoginRedirect, { replace: true })
          return
        }
      }

      const pendingPlan = localStorage.getItem('pendingPlan')
      if (pendingPlan) {
        localStorage.removeItem('pendingPlan')
        const planParam = pendingPlan !== 'true' ? `?plan=${pendingPlan}` : ''
        navigate(`/billing${planParam}`, { replace: true })
      } else {
        navigate('/dashboard', { replace: true })
      }
    }
  }, [user, authLoading, navigate])

  // Don't render login form while checking auth status or if already logged in
  if (authLoading || user) {
    return (
      <div className="login-page__loading">
        <p>{t('common:buttons.loading')}</p>
      </div>
    )
  }

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    if (!email.trim()) {
      setError(t('errors.emailRequired'))
      return
    }

    setError('')
    setLoading(true)

    try {
      const result = await authService.getLoginMethod(email)
      setLoginMethod(result)

      if (result.method === 'locked') {
        // Account is locked
        setStep('locked')
      } else if (result.method === 'unverified') {
        // Email not verified
        setStep('unverified')
      } else if (result.method === 'sso') {
        // Tenant has SSO enabled - redirect to SSO
        setStep('sso')
      } else if (result.method === 'both') {
        // Both SSO and password available
        setStep('both')
      } else {
        // Password only
        setStep('password')
      }
    } catch (err) {
      setError(t('login.unableToCheckLoginMethod'))
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const result = await login(email, password)

    if (result.success) {
      return
    } else if (result.requiresPasswordChange) {
      navigate('/change-password', {
        state: {
          user: result.user,
          cognitoUser: result.signInResult
        }
      })
    } else if (result.requiresMfaVerification) {
      navigate('/verify-mfa', {
        state: {
          user: result.user
        }
      })
    } else {
      // Login failed - report to backend for lockout tracking
      const lockoutStatus = await authService.reportLoginFailed(email)

      if (lockoutStatus.locked) {
        setError(lockoutStatus.message || t('login.accountLocked'))
        // Switch to locked step to prevent further attempts
        setLoginMethod({ method: 'locked', ...lockoutStatus })
        setStep('locked')
      } else {
        setError(result.error || t('login.loginFailed'))
      }
    }

    setLoading(false)
  }

  const handleSsoLogin = async () => {
    if (loginMethod?.cognitoProviderName) {
      try {
        setLoading(true)

        // GET /auth/sso/start — server builds PKCE URL, returns { authUrl, codeVerifier }
        const params = new URLSearchParams({
          provider: loginMethod.cognitoProviderName,
          ...(email ? { email } : {}),
        })
        const response = await api.get(`/auth/sso/start?${params.toString()}`)
        const { authUrl, codeVerifier } = response.data

        // Store verifier for token exchange after callback
        sessionStorage.setItem('sso_pkce_verifier', codeVerifier)

        window.location.href = authUrl
      } catch (err) {
        console.error('SSO redirect error:', err)
        setError(t('errors.ssoError'))
        setLoading(false)
      }
    } else if (loginMethod?.ssoUrl) {
      // Fallback to manual redirect if provider name not available
      window.location.href = loginMethod.ssoUrl
    }
  }

  const handleResendVerification = async () => {
    setResendLoading(true)
    setResendSuccess('')
    setError('')

    try {
      await authService.resendVerification(email)
      setResendSuccess(t('demoSignup.verificationResent'))
    } catch (err) {
      setError(err.response?.data?.message || t('demoSignup.resendFailed'))
    } finally {
      setResendLoading(false)
    }
  }

  const handleBack = () => {
    setStep('email')
    setPassword('')
    setError('')
    setResendSuccess('')
    setLoginMethod(null)
  }

  return (
    <div className="login-page">
      <MarketingHeader />
      <div className="login-page__card">
        <h1 className="login-page__title">{t('login.title')}</h1>

        {successMessage && (
          <div className="login-page__success">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="login-page__error">
            {error}
          </div>
        )}

        {/* Step 1: Email entry */}
        {step === 'email' && (
          <form onSubmit={handleEmailSubmit}>
            <div className="login-page__field">
              <label>{t('login.emailLabel')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="login-page__button login-page__button--primary"
              disabled={loading}
            >
              {loading ? t('login.checking') : t('login.continue')}
            </button>

            <div className="login-page__links">
              <Link to="/forgot-password">{t('login.forgotPassword')}</Link>
            </div>
          </form>
        )}

        {/* Step 2: Password entry (password-only or optional SSO) */}
        {step === 'password' && (
          <form onSubmit={handlePasswordSubmit}>
            <div className="login-page__email-display">
              <span>{email}</span>
              <button type="button" onClick={handleBack} className="login-page__change-email">
                {t('login.changeEmail')}
              </button>
            </div>

            <div className="login-page__field">
              <label>{t('login.passwordLabel')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="login-page__button login-page__button--primary"
              disabled={loading}
            >
              {loading ? t('login.signingIn') : t('login.submitButton')}
            </button>

            <div className="login-page__links">
              <Link to="/forgot-password">{t('login.forgotPassword')}</Link>
            </div>
          </form>
        )}

        {/* Step 3: SSO only (enforced) */}
        {step === 'sso' && (
          <div>
            <div className="login-page__email-display">
              <span>{email}</span>
              <button type="button" onClick={handleBack} className="login-page__change-email">
                {t('login.changeEmail')}
              </button>
            </div>

            <div className="login-page__sso-message">
              {t('login.ssoRequired')}
            </div>

            <button
              type="button"
              onClick={handleSsoLogin}
              className="login-page__button login-page__button--sso"
              disabled={loading}
            >
              {t('login.ssoButton')}
            </button>
          </div>
        )}

        {/* Step 4: Both options available */}
        {step === 'both' && (
          <div>
            <div className="login-page__email-display">
              <span>{email}</span>
              <button type="button" onClick={handleBack} className="login-page__change-email">
                {t('login.changeEmail')}
              </button>
            </div>

            <button
              type="button"
              onClick={handleSsoLogin}
              className="login-page__button login-page__button--sso"
              disabled={loading}
            >
              {t('login.ssoButton')}
            </button>

            <div className="login-page__divider">
              <span>{t('login.orContinueWith')}</span>
            </div>

            <form onSubmit={handlePasswordSubmit}>
              <div className="login-page__field">
                <label>{t('login.passwordLabel')}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="login-page__button login-page__button--secondary"
                disabled={loading}
              >
                {loading ? t('login.signingIn') : t('login.signInWithPassword')}
              </button>

              <div className="login-page__links">
                <Link to="/forgot-password">{t('login.forgotPassword')}</Link>
              </div>
            </form>
          </div>
        )}

        {/* Step 5: Email Unverified */}
        {step === 'unverified' && (
          <div>
            <div className="login-page__email-display">
              <span>{email}</span>
              <button type="button" onClick={handleBack} className="login-page__change-email">
                {t('login.changeEmail')}
              </button>
            </div>

            <div className="login-page__unverified-message">
              <p>{t('login.unverifiedMessage')}</p>
            </div>

            {resendSuccess && (
              <div className="login-page__success">
                {resendSuccess}
              </div>
            )}

            <button
              type="button"
              onClick={handleResendVerification}
              className="login-page__button login-page__button--primary"
              disabled={resendLoading}
            >
              {resendLoading ? t('demoSignup.resending') : t('login.resendVerification')}
            </button>

            <div className="login-page__links">
              <button type="button" onClick={handleBack} className="login-page__change-email">
                {t('login.back')}
              </button>
            </div>
          </div>
        )}

        {/* Step 6: Account Locked */}
        {step === 'locked' && (
          <div>
            <div className="login-page__email-display">
              <span>{email}</span>
              <button type="button" onClick={handleBack} className="login-page__change-email">
                Change
              </button>
            </div>

            <div className="login-page__locked-message">
              <div className="login-page__locked-icon">🔒</div>
              <h2>Account Temporarily Locked</h2>
              <p>{loginMethod?.message || 'Too many failed login attempts. Please try again later.'}</p>
              {loginMethod?.lockedUntil && (
                <p className="login-page__locked-time">
                  Locked until: {new Date(loginMethod.lockedUntil).toLocaleTimeString()}
                </p>
              )}
            </div>

            <div className="login-page__links">
              <Link to="/forgot-password">Reset your password</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LoginPage
